// client/src/pages/VideoGate/index.jsx - שער וידאו מוגן עם שאלון אופציונלי
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthState, useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import customFetch from '../../api/customFetch';
import ReactPlayer from 'react-player';

const isYouTube = (url='') => /youtube\.com|youtu\.be/i.test(url);

// Loading component
const LoadingScreen = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    background: 'var(--bg)',
    color: 'var(--text)'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '1.2rem', marginBottom: '10px' }}>טוען...</div>
      <div style={{ color: 'var(--text)' }}>טוען וידאו</div>
    </div>
  </div>
);

export default function VideoGate() {
  const { state, dispatch } = useApp();
  const { slug } = useParams();
  const nav = useNavigate();
  const { isLoggedIn, user, loading } = useAuthState();
  const { logout } = useAuth();
  const html5Ref = useRef(null);
  const [progress, setProgress] = useState(0);
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    if (loading) return;
    
    if (!isLoggedIn) {
      nav('/user/login');
      return;
    }

    // טען קורס אם נכנסו ישירות ל/video
    (async () => {
      try {
        if (!state.course) {
          const { data } = await customFetch.get(`/courses/${slug}`);
          dispatch({ type: 'LOAD_COURSE', payload: data });
        }
      } catch (error) {
        console.error('Error loading course:', error);
        if (error.response?.status === 401) {
          await logout();
          nav('/user/login');
        }
      }
    })();
  }, [slug, state.course, dispatch, nav, isLoggedIn, loading, logout]);

  // ----- HTML5 video (MP4/HLS) -----
  const onEndedHTML5 = () => {
    dispatch({ type: 'UNLOCK_START' });
  };
  
  const onTimeUpdateHTML5 = () => {
    const el = html5Ref.current;
    if (!el || !el.duration) return;
    const p = Math.round((el.currentTime / el.duration) * 100);
    setProgress(p);
    if (p >= 95) {
      dispatch({ type: 'UNLOCK_START' });
    }
  };

  // ----- YouTube (ReactPlayer) -----
  const onProgressYT = ({ played }) => {
    const p = Math.round(played * 100);
    setProgress(p);
    if (p >= 95) {
      dispatch({ type: 'UNLOCK_START' });
    }
  };
  
  const onEndedYT = () => {
    dispatch({ type: 'UNLOCK_START' });
  };

  // פונקציה לטיפול בלחיצה על "המשך" אחרי הוידאו
  const handleContinueAfterVideo = async () => {
    try {
      const hasQuestions = state.course?.questions && state.course.questions.length > 0;
      
      if (hasQuestions) {
        // יש שאלות - מעבר לשאלון
        nav(`/${slug}/quiz`);
      } else {
        // אין שאלות - סיום הקורס
        await customFetch.post(`/progress/${slug}/complete`);
        nav('/user/dashboard');
      }
    } catch (error) {
      console.error('Error completing course:', error);
      // במקרה של שגיאה, נמשיך בכל זאת
      nav('/user/dashboard');
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isLoggedIn) {
    return null;
  }

  const videoUrl = state.course?.videoUrl || '';
  const hasQuestions = state.course?.questions && state.course.questions.length > 0;

  return (
    <div className="page" dir="rtl">
      {/* הצגת פרטי המשתמש והקורס */}
      <div style={{
        background: 'var(--card)',
        padding: '16px 20px',
        borderRadius: '16px',
        marginBottom: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
      }}>
        <div>
          <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--text)' }}>
            {state.course?.title || '...'}
          </div>
          <div style={{ fontSize: '0.9rem', color: 'var(--text)' }}>
            לומד: {user?.fullName || user?.username}
          </div>
        </div>
        <button 
          onClick={() => nav('/user/dashboard')}
          style={{
            background: 'var(--lt-bad)',
            color: 'var(--bg)',
            border: 'none',
            padding: '10px 16px',
            borderRadius: '16px',
            cursor: 'pointer',
            fontSize: '0.9rem',
            fontWeight: '700'
          }}
        >
          חזור לדשבורד
        </button>
      </div>

      <div style={{ maxWidth: 1100 }}>
        {isYouTube(videoUrl) ? (
          <div style={{ position:'relative', paddingTop: '56.25%' }}>
            <ReactPlayer
              url={videoUrl}
              playing
              muted      // מאפשר autoplay
              controls
              width="100%"
              height="100%"
              style={{ position:'absolute', inset:0, borderRadius: 16, overflow: 'hidden' }}
              onProgress={onProgressYT}
              onEnded={onEndedYT}
              onStart={() => setBlocked(false)}
              onError={() => setBlocked(true)}
            />
          </div>
        ) : (
          <video
            ref={html5Ref}
            src={videoUrl}
            controls
            autoPlay
            // muted
            playsInline
            onEnded={onEndedHTML5}
            onTimeUpdate={onTimeUpdateHTML5}
            preload="metadata"
            style={{ width: '100%', borderRadius: 16 }}
            onLoadedMetadata={() => html5Ref.current?.play().catch(()=>setBlocked(true))}
            onError={() => setBlocked(true)}
          />
        )}
      </div>

      <div style={{ 
        marginTop: 16, 
        padding: '16px',
        background: progress >= 95 ? 'var(--card)' : 'var(--card)',
        border: progress >= 95 ? '1px solid #27ae60' : '1px solid var(--accent)',
        borderRadius: '16px',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: 'var(--text)' }}>
          צפייה: {progress}%
        </div>
        {progress >= 95 ? (
          <div style={{ color: '#27ae60', fontSize: '0.9rem' }}>
            {hasQuestions ? 'סיימת לצפות! אתה יכול להמשיך לשאלון' : 'סיימת לצפות! הקורס הושלם בהצלחה'}
          </div>
        ) : (
          <div style={{ color: 'var(--text)', fontSize: '0.9rem' }}>
            צפה לפחות ב-95% מהסרטון כדי להמשיך
          </div>
        )}
      </div>

      {hasQuestions ? (
        // יש שאלות - כפתור למעבר לשאלון
        <button
          className="btn"
          disabled={state.lockedUntilVideoEnds}
          onClick={() => nav(`/${slug}/quiz`)}
          style={{ 
            marginTop: 16,
            background: state.lockedUntilVideoEnds ? 'var(--text)' : 'var(--accent)',
            color: state.lockedUntilVideoEnds ? 'var(--bg)' : '#0b1c2b',
            opacity: state.lockedUntilVideoEnds ? 0.6 : 1
          }}
        >
          {state.lockedUntilVideoEnds ? 'המשך לשאלון (נעול)' : 'המשך לשאלון'}
        </button>
      ) : (
        // אין שאלות - כפתור לסיום הקורס
        <button
          className="btn"
          disabled={state.lockedUntilVideoEnds}
          onClick={handleContinueAfterVideo}
          style={{ 
            marginTop: 16,
            background: state.lockedUntilVideoEnds ? 'var(--text)' : '#27ae60',
            color: state.lockedUntilVideoEnds ? 'var(--bg)' : 'white',
            opacity: state.lockedUntilVideoEnds ? 0.6 : 1
          }}
        >
          {state.lockedUntilVideoEnds ? 'סיום הקורס (נעול)' : 'סיים את הקורס'}
        </button>
      )}

      {!hasQuestions && progress >= 95 && (
        <div style={{
          marginTop: 16,
          padding: '16px',
          background: 'var(--card)',
          border: '1px solid #27ae60',
          borderRadius: '16px',
          textAlign: 'center'
        }}>
          <div style={{ color: '#27ae60', fontSize: '1.1rem', fontWeight: 'bold' }}>
            הקורס הושלם!
          </div>
          <div style={{ color: 'var(--text)', fontSize: '0.9rem', marginBottom: '12px' }}>
            אין שאלות לקורס זה. סיימת בהצלחה.
          </div>
        </div>
      )}

      {blocked && (
        <div style={{
          marginTop: 16,
          padding: '16px',
          background: 'var(--card)',
          color: '#ff6b6b',
          border: '1px solid #ff6b6b',
          borderRadius: '16px',
          textAlign: 'center'
        }}>
          אם הווידאו לא מתחיל, לחץ Play בנגן.
        </div>
      )}
    </div>
  );
}